import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import bgImage from "../../assets/Images/Breadcrumbs.webp";
import { Home, Star } from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaShoppingCart,
  FaWhatsapp,
  FaStar,
  FaRegStar,
  FaStarHalfAlt,
  FaArrowLeft,
  FaShareAlt,
} from "react-icons/fa";
import {
  AiOutlineHeart,
  AiFillHeart,
  AiOutlineShoppingCart,
} from "react-icons/ai";
import { toast } from "react-toastify";
import ProductDetailsSkeleton from "./ProductDetailsSkeleton";
import { IoArrowRedoSharp } from "react-icons/io5";
import ProductReviews from "./ProductReviews";

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [productDetail, setProductDetail] = useState({});
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [processingWishlist, setProcessingWishlist] = useState({});
  const [userData, setUserData] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef();

  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const getUserData = async () => {
      const storedUserData = localStorage.getItem("BricksNbar-Users-Data");
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
        fetchWishlist(parsedUserData._id);
      }
    };

    getUserData();
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([productDetailsById()]);
    };

    fetchData();
  }, [productId]);

  useEffect(() => {
    if (productDetail.subCategory?._id) {
      fetchRelatedProducts(productDetail.subCategory._id);
    }
  }, [productDetail.subCategory]);

  useEffect(() => {
    if (
      productDetail.sizeType === "multiple" &&
      productDetail.multipleSizes?.length > 0
    ) {
      setSelectedSize(productDetail.multipleSizes[0]);
    }
  }, [productDetail]);

  const handleMouseEnter = () => {
    setShowMagnifier(true);
  };

  const handleMouseLeave = () => {
    setShowMagnifier(false);
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    setMagnifierPosition({ x: xPercent, y: yPercent });
    setImagePosition({ x, y });
  };

  const fetchWishlist = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wishlist/wishlist-by-user/${userId}`
      );

      if (response.data && response.data.products) {
        const wishlistProductIds = response.data.products.map(
          (product) => product._id
        );
        setWishlist(wishlistProductIds);
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    }
  };

  const isInWishlist = (productId) => {
    return wishlist.includes(productId);
  };

  const handleAddToWishlist = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingWishlist({ ...processingWishlist, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/wishlist/add-to-wishlist`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setWishlist([...wishlist, productId]);
      }
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      toast.error("Failed to add to wishlist");
    } finally {
      setProcessingWishlist({ ...processingWishlist, [productId]: false });
    }
  };

  const handleRemoveFromWishlist = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingWishlist({ ...processingWishlist, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/wishlist/remove-from-wishlist`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setWishlist(wishlist.filter((id) => id !== productId));
      }
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      toast.error("Failed to remove from wishlist");
    } finally {
      setProcessingWishlist({ ...processingWishlist, [productId]: false });
    }
  };

  const handleToggleWishlist = (event, productId) => {
    if (isInWishlist(productId)) {
      handleRemoveFromWishlist(event, productId);
    } else {
      handleAddToWishlist(event, productId);
    }
  };

  const productDetailsById = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/get-product-by-id/${productId}`
      );
      setProductDetail(response.data);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRelatedProducts = async (subCategoryId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/products-by-sub-category/${subCategoryId}`
      );
      const filteredProducts = response.data.filter(
        (product) => product._id !== productId
      );
      setRelatedProducts(filteredProducts);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleNavigateToProductDetails = (productId) => {
    navigate(`/product-details/${productId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActiveImage(0);
    setQuantity(1);
  };

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} className="h-4 w-4" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className="h-4 w-4" />);
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaRegStar key={`empty-${i}`} className="h-4 w-4" />);
    }

    return stars;
  };

  const getCurrentPrice = () => {
    if (productDetail.sizeType === "one" && productDetail.singleSize) {
      return productDetail.singleSize.price;
    } else if (productDetail.sizeType === "multiple" && selectedSize) {
      return selectedSize.price;
    }
    return 0;
  };

  const getPreviousPrice = () => {
    if (productDetail.sizeType === "one" && productDetail.singleSize) {
      return productDetail.singleSize.previous_price;
    } else if (productDetail.sizeType === "multiple" && selectedSize) {
      return selectedSize.previous_price;
    }
    return null;
  };

  const getRelatedProductPrice = (product) => {
    if (product.sizeType === "one" && product.singleSize) {
      return {
        price: product.singleSize.price,
        previous_price: product.singleSize.previous_price,
      };
    } else if (
      product.sizeType === "multiple" &&
      product.multipleSizes?.length > 0
    ) {
      return {
        price: product.multipleSizes[0].price,
        previous_price: product.multipleSizes[0].previous_price,
      };
    }
    return { price: 0, previous_price: null };
  };

  const handleAddToCart = async (event, productId) => {
    event.stopPropagation();

    if (productDetail.sizeType === "multiple" && !selectedSize) {
      toast.error("Please select a size");
      return;
    }

    try {
      setAddingToCart({ ...addingToCart, [productId]: true });

      const userData = localStorage.getItem("BricksNbar-Users-Data");
      const userId = userData ? JSON.parse(userData)._id : null;

      if (!userId) {
        navigate("/sign-in");
        return;
      }

      const cartData = {
        userId: userId,
        productId: productId,
        quantity: quantity,
      };

      if (productDetail.sizeType === "multiple" && selectedSize) {
        cartData.sizeId = selectedSize._id;
        cartData.selectedSize = selectedSize.size;
      }

      const config = {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/cart/add-product-to-cart`,
        headers: {
          "Content-Type": "application/json",
        },
        data: cartData,
      };

      const response = await axios.request(config);

      console.log("Product added to cart:", response.data);
      toast.success("Product added to cart!");
      window.location.reload();
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add product to cart");
    } finally {
      setAddingToCart({ ...addingToCart, [productId]: false });
    }
  };

  const handleAddToCartRelated = async (event, productId) => {
    event.stopPropagation();

    try {
      setAddingToCart({ ...addingToCart, [productId]: true });

      const userData = localStorage.getItem("BricksNbar-Users-Data");
      const userId = userData ? JSON.parse(userData)._id : null;

      if (!userId) {
        navigate("/sign-in");
        return;
      }

      const config = {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/cart/add-product-to-cart`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          userId: userId,
          productId: productId,
          quantity: 1,
        },
      };

      const response = await axios.request(config);

      console.log("Product added to cart:", response.data);
      toast.success("Product added to cart!");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add product to cart");
    } finally {
      setAddingToCart({ ...addingToCart, [productId]: false });
    }
  };

  const handleShareProduct = (platform) => {
    const productUrl = window.location.href;

    const productTitle = productDetail.name;
    const shareMessage = `Check out this product: ${productTitle}`;

    let shareUrl;

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          productUrl
        )}`;
        break;

      case "whatsapp":
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
          shareMessage + " " + productUrl
        )}`;
        break;

      default:
        return;
    }

    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied! Now share it via Instagram DM.");
  };

  if (loading) return <ProductDetailsSkeleton />;

  return (
    <>
      <div
        className="bg-gray-800 py-12 bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center text-white">
            <Home
              size={20}
              className="hover:text-orange-500 cursor-pointer"
              onClick={() => {
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
            <span className="mx-2">›</span>
            <p className="hover:text-orange-500">Category</p>
            <span className="mx-2">›</span>
            <p className="hover:text-orange-500">
              {productDetail.category?.name}
            </p>
            <span className="mx-2">›</span>
            <span className="text-orange-500">{productDetail.name}</span>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full md:w-1/2">
            <div className="flex flex-col-reverse md:flex-row gap-4">
              <div className="flex md:flex-col gap-4 mt-4 md:mt-0">
                {productDetail.images?.map((image, index) => (
                  <div
                    key={index}
                    className={`w-16 h-16 border-2 rounded-xl p-1 ${
                      activeImage === index
                        ? "border-orange-500"
                        : "border-gray-200"
                    } cursor-pointer`}
                    onClick={() => setActiveImage(index)}
                  >
                    <img
                      src={image}
                      alt={`${productDetail.name} thumbnail ${index + 1}`}
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                ))}
              </div>

              <div className="flex-1 relative">
                <div className="border border-gray-200 p-2 h-[600px] rounded-xl relative overflow-hidden">
                  <img
                    ref={imageRef}
                    src={productDetail?.images?.[activeImage]}
                    alt={productDetail?.name}
                    className="w-full h-full object-fill rounded-xl cursor-crosshair"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                  />

                  {showMagnifier && (
                    <div
                      className="absolute pointer-events-none border-2 border-orange-400 bg-white bg-opacity-30"
                      style={{
                        left: `${imagePosition.x - 75}px`,
                        top: `${imagePosition.y - 75}px`,
                        width: "150px",
                        height: "150px",
                        borderRadius: "50%",
                        boxShadow: "0 0 0 2000px rgba(0,0,0,0.3)",
                        zIndex: 10,
                      }}
                    />
                  )}
                </div>

                {showMagnifier && (
                  <div
                    className="absolute top-0 left-full ml-4 w-[600px] h-[600px] border-2 border-gray-300 rounded-xl overflow-hidden bg-white shadow-xl z-20 hidden lg:block"
                    style={{
                      backgroundImage: `url(${productDetail?.images?.[activeImage]})`,
                      backgroundSize: "300%",
                      backgroundPosition: `${magnifierPosition.x}% ${magnifierPosition.y}%`,
                      backgroundRepeat: "no-repeat",
                    }}
                  ></div>
                )}
              </div>
            </div>

            {showMagnifier && (
              <div className="lg:hidden fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
                <div
                  className="w-full h-full max-w-md max-h-md"
                  style={{
                    backgroundImage: `url(${productDetail?.images?.[activeImage]})`,
                    backgroundSize: "150%",
                    backgroundPosition: `${magnifierPosition.x}% ${magnifierPosition.y}%`,
                    backgroundRepeat: "no-repeat",
                  }}
                />
                <button
                  className="absolute top-4 right-4 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center"
                  onClick={() => setShowMagnifier(false)}
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="w-full md:w-1/2">
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm text-gray-500">
                Brand:{" "}
                <span className="font-medium">{productDetail.brand}</span>
              </div>

              <div className="relative inline-block" ref={popupRef}>
                <div
                  aria-label="Share"
                  role="button"
                  tabIndex={0}
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center text-gray-600 gap-2 hover:text-[#C23F00] transition duration-200 cursor-pointer"
                >
                  <IoArrowRedoSharp size={20} /> <span>Share</span>
                </div>

                {isOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded shadow-lg p-3 flex justify-around items-center z-50">
                    <div
                      className="p-2 rounded-full bg-blue-600 text-white cursor-pointer hover:bg-blue-700"
                      onClick={() => handleShareProduct("facebook")}
                      role="button"
                      tabIndex={0}
                    >
                      <FaFacebookF size={20} />
                    </div>
                    <div
                      className="p-2 rounded-full bg-green-600 text-white cursor-pointer hover:bg-green-700"
                      onClick={() => handleShareProduct("whatsapp")}
                      role="button"
                      tabIndex={0}
                    >
                      <FaWhatsapp size={20} />
                    </div>
                    <div
                      className="p-2 rounded-full bg-pink-600 text-white cursor-pointer hover:bg-pink-700"
                      onClick={() => handleCopyLink("instagram")}
                      role="button"
                      tabIndex={0}
                    >
                      <FaInstagram size={20} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className=" mb-2 flex justify-start items-center gap-2">
              <h1 className="text-lg font-medium capitalize">
                {productDetail.name}
                {productDetail.brand && ` (${productDetail.brand})`}
                {productDetail.measurement && ` (${productDetail.measurement})`}
              </h1>
            </div>

            <div className="flex flex-col items-start mb-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={18}
                    aria-label={`${star} Star`}
                    className={`transition-all ${
                      star <= Math.round(productDetail.ratings || 0)
                        ? "text-[#C23F00] fill-[#C23F00] drop-shadow-sm"
                        : "text-gray-300"
                    }`}
                  />
                ))}

                <span className="ml-2 flex items-center text-sm font-medium text-white bg-[#C23F00] px-2 py-0.5 rounded-md">
                  {Number(productDetail.ratings || 0).toFixed(1)}
                  <span className="ml-1 text-gray-200 font-normal">/ 5</span>
                </span>
              </div>

              <span className="mt-1 text-xs text-gray-500">
                Customer Ratings
              </span>
            </div>

            <div className="mb-3 p-4 bg-gray-50 rounded-xl border border-gray-200 shadow-sm w-fit">
              <div className="mt-1 flex items-center gap-4">
                <div className="text-3xl font-bold text-gray-900">
                  ₹{getCurrentPrice().toFixed(2)}
                </div>
                {getPreviousPrice() &&
                  getPreviousPrice() > getCurrentPrice() && (
                    <div className="flex items-center gap-2">
                      <span className="text-base text-gray-400 line-through">
                        ₹{getPreviousPrice().toFixed(2)}
                      </span>
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-md">
                        {Math.round(
                          ((getPreviousPrice() - getCurrentPrice()) /
                            getPreviousPrice()) *
                            100
                        )}
                        % OFF
                      </span>
                    </div>
                  )}
                <span className="font-semibold">(incl. of GST)</span>
              </div>

              {getPreviousPrice() && (
                <div className="mt-1 text-sm text-green-600 font-medium">
                  You save ₹
                  {(getPreviousPrice() - getCurrentPrice()).toFixed(2)}
                </div>
              )}
            </div>

            {productDetail.sizeType === "multiple" &&
              productDetail.multipleSizes?.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm tracking-wide font-medium mb-2">
                    Available Sizes
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {productDetail.multipleSizes.map((sizeOption) => (
                      <button
                        key={sizeOption._id}
                        type="button"
                        onClick={() => setSelectedSize(sizeOption)}
                        className={`min-w-[80px] px-4 py-2 border rounded-xl text-center transition-colors duration-150
            ${
              selectedSize?._id === sizeOption._id
                ? "border-[#C23F00] bg-[#fcf7f4] text-[#C23F00] font-semibold shadow-sm"
                : "border-gray-300 text-gray-700 hover:border-[#e2916c] hover:bg-gray-50"
            }`}
                      >
                        <div className="text-sm">{sizeOption.size}</div>
                        <div className="text-xs mt-1 text-gray-600">
                          ₹{sizeOption.price.toFixed(2)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

            {productDetail.stock_status === "active" && (
              <div className="mb-3 w-full border border-gray-200 rounded-2xl p-4 shadow-sm bg-white">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  {/* Quantity Selector */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-gray-700">
                      Quantity:
                    </span>
                    <div className="flex items-center border border-gray-300 rounded-full overflow-hidden">
                      <button
                        onClick={decreaseQuantity}
                        className="px-4 py-2 bg-gray-100 hover:bg-[#C23F00] hover:text-white text-lg font-bold transition-colors"
                      >
                        −
                      </button>
                      <input
                        type="text"
                        value={quantity}
                        min={1}
                        onChange={(e) =>
                          setQuantity(parseInt(e.target.value) || 1)
                        }
                        className="w-14 text-center text-base py-2 border-x border-gray-200 outline-none"
                      />
                      <button
                        onClick={increaseQuantity}
                        className="px-4 py-2 bg-gray-100 hover:bg-[#C23F00] hover:text-white text-lg font-bold transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    {/* Add to Cart */}
                    <button
                      onClick={(e) => handleAddToCart(e, productDetail._id)}
                      disabled={addingToCart[productDetail._id]}
                      className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 ${
                        addingToCart[productDetail._id]
                          ? "bg-[#C23F00]/60 cursor-not-allowed"
                          : "bg-[#C23F00] hover:bg-[#a53700]"
                      }`}
                    >
                      {addingToCart[productDetail._id] ? (
                        <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      ) : (
                        <>
                          <FaShoppingCart size={18} />
                          Add to Cart
                        </>
                      )}
                    </button>

                    {/* Wishlist */}
                    <button
                      onClick={(e) =>
                        handleToggleWishlist(e, productDetail._id)
                      }
                      disabled={processingWishlist[productDetail._id]}
                      className={`w-12 h-12 flex items-center justify-center rounded-full border transition-colors duration-200 ${
                        isInWishlist(productDetail._id)
                          ? "bg-red-100 border-red-200 text-red-500"
                          : "bg-white border-gray-300 text-gray-400 hover:text-red-500 hover:border-red-300"
                      }`}
                    >
                      {processingWishlist[productDetail._id] ? (
                        <span className="h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></span>
                      ) : isInWishlist(productDetail._id) ? (
                        <AiFillHeart size={22} />
                      ) : (
                        <AiOutlineHeart size={22} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {productDetail.stock_status !== "active" && (
              <div className="mb-3 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl">
                <span className="font-semibold">Out of Stock</span>
                <p className="text-sm mt-1">
                  This product is currently unavailable.
                </p>
              </div>
            )}

            <div className="pt-1 px-4 sm:px-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Category */}
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <p className="text-sm text-gray-500 font-semibold mb-1">
                    Category
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {productDetail?.category?.name || "—"}
                  </p>
                </div>

                {/* Sub-Category */}
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <p className="text-sm text-gray-500 font-semibold mb-1">
                    Sub-Category
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {productDetail?.subCategory?.name || "—"}
                  </p>
                </div>
              </div>

              {/* Features */}
              {productDetail.features?.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <p className="text-sm text-gray-500 font-semibold mb-2">
                    Features
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {productDetail.features.map((feature, index) => (
                      <span
                        key={index}
                        className="text-sm px-3 py-1 bg-white border border-gray-300 rounded-full text-gray-700"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3">
                <p className="text-gray-500 font-light tracking-wide text-sm leading-6">
                  ➡️ &nbsp;{productDetail.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 py-8 border-t border-gray-200 mt-8">
        <h2 className="text-2xl font-bold mb-6">Related Products</h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="border border-gray-200 rounded-md overflow-hidden shadow-sm p-4"
              >
                <div className="h-48 bg-gray-200 animate-pulse mb-4"></div>
                <div className="h-4 bg-gray-200 animate-pulse mb-2 w-1/3"></div>
                <div className="h-6 bg-gray-200 animate-pulse mb-4"></div>
                <div className="h-4 bg-gray-200 animate-pulse w-1/2"></div>
              </div>
            ))}
          </div>
        ) : relatedProducts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {relatedProducts.map((product) => {
              const productPricing = getRelatedProductPrice(product);
              return (
                <div
                  key={product._id}
                  className="border border-gray-200 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow hover:border-orange-700 cursor-pointer"
                  onClick={() => handleNavigateToProductDetails(product._id)}
                >
                  <div className="relative">
                    {product.stock_status === "out of stock" && (
                      <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                        Out Of Stock
                      </span>
                    )}

                    <div className="h-52 flex items-center justify-center bg-white p-2 rounded-t-md border border-b-gray-200">
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="max-h-full max-w-full object-contain rounded-md"
                      />
                    </div>

                    <button
                      className="absolute top-3 right-2 p-1 rounded-full bg-white shadow hover:bg-gray-100"
                      onClick={(e) => handleToggleWishlist(e, product._id)}
                      disabled={processingWishlist[product._id]}
                    >
                      {processingWishlist[product._id] ? (
                        <span className="block h-5 w-5 border-t-2 border-r-2 border-gray-700 rounded-full animate-spin"></span>
                      ) : isInWishlist(product._id) ? (
                        <AiFillHeart className="text-red-500" size={20} />
                      ) : (
                        <AiOutlineHeart className="text-gray-500" size={20} />
                      )}
                    </button>
                  </div>
                  <div className="p-4">
                    <h2 className="font-medium text-gray-800 capitalize">
                      {product.name}
                    </h2>
                    <h3 className="text-sm text-gray-500 capitalize">
                      {product.category?.name || "Category"}
                    </h3>

                    <div className="flex items-center justify-start gap-2">
                      <div>
                        <span className="font-bold text-gray-800">
                          ₹ {productPricing.price}
                        </span>
                        {productPricing.previous_price && (
                          <span className="text-gray-500 line-through text-sm ml-2">
                            ₹ {productPricing.previous_price}
                          </span>
                        )}
                      </div>
                      {product?.measurement && (
                        <p className="font-medium text-gray-600 capitalize text-sm">
                          ({product.measurement})
                        </p>
                      )}
                    </div>
                    <div className="flex items-center text-yellow-400">
                      {renderRatingStars(product.ratings || 0)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No related products found
          </div>
        )}
      </div>

      {productDetail && (
        <ProductReviews
          reviews={productDetail.reviews || []}
          averageRating={productDetail.averageRating || 0}
          reviewCount={productDetail.reviewCount || 0}
        />
      )}
    </>
  );
};

export default ProductDetails;
